import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, MenuItem, Paper, Select, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSelector } from 'react-redux';

import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import MessagesPaneHeader from '../../components/MessagesPaneHeader';
import UsageTrendChart from '../../components/usage/UsageTrendChart';
import UsageLogTable from '../../components/usage/UsageLogTable';
import { RootState } from '../../store/AriStore';
import {
  RECENT_12,
  exportUsageList,
  exportUsageTrend,
  getUsageList,
  getUsageTrend,
} from '../../api/usage';
import type { UsageList, UsageTrend } from '../../api/usage';
import styles from '../../css/usageDashboard.module.css';

const PAGE_SIZE = 50;

function comma(n: number): string {
  return n.toLocaleString('ko-KR');
}

/** "2026-08" -> "2026년 8월" */
function monthTitle(month: string): string {
  const [year, mm] = month.split('-');
  return `${year}년 ${parseInt(mm, 10)}월`;
}

function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function UsageDashboard() {
  const menuInfo = useSelector((state: RootState) => state.menu);

  const [period, setPeriod] = useState<string>(RECENT_12);
  const [trend, setTrend] = useState<UsageTrend | null>(null);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthKey());
  const [page, setPage] = useState(1);
  const [list, setList] = useState<UsageList | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [exporting, setExporting] = useState<'trend' | 'list' | null>(null);

  /** 기간 선택지는 데이터가 있는 연도에서 뽑는다 */
  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return [now, now - 1];
  }, []);

  /** 조회 월 선택지 — 추이에 담긴 달 중 데이터가 있는 것만, 최신순 */
  const monthOptions = useMemo(() => {
    if (!trend) return [];
    return trend.points
      .filter((p) => p.queries > 0 || p.mau > 0)
      .map((p) => p.month)
      .reverse();
  }, [trend]);

  // ── 추이 ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    getUsageTrend(period)
      .then((data) => {
        if (cancelled) return;
        setTrend(data);
        setTrendError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('이용 추이 조회 실패:', e);
        setTrendError('이용 추이를 불러오지 못했습니다.');
      });

    return () => { cancelled = true; };
  }, [period]);

  /**
   * 선택된 달이 현재 추이 구간에 없으면 가장 최근 달로 옮긴다.
   * 기간을 바꿨는데 표가 빈 달을 가리키고 있는 상태를 막는다.
   */
  useEffect(() => {
    if (!monthOptions.length) return;
    if (!monthOptions.includes(selectedMonth)) {
      setSelectedMonth(monthOptions[0]);
      setPage(1);
    }
  }, [monthOptions, selectedMonth]);

  // ── 질의 내역 ─────────────────────────────────────────
  useEffect(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    if (!year || !month) return;

    let cancelled = false;
    setListLoading(true);

    getUsageList(year, month, page, PAGE_SIZE)
      .then((data) => {
        if (cancelled) return;
        setList(data);
        setListError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('질의 내역 조회 실패:', e);
        setListError('질의 내역을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedMonth, page]);

  const handleSelectMonth = useCallback((month: string) => {
    setSelectedMonth(month);
    setPage(1);
  }, []);

  const handleExportTrend = async () => {
    setExporting('trend');
    try {
      await exportUsageTrend(period);
    } catch (e) {
      console.error('추이 엑셀 내려받기 실패:', e);
      setTrendError('엑셀을 내려받지 못했습니다.');
    } finally {
      setExporting(null);
    }
  };

  const handleExportList = async () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    setExporting('list');
    try {
      await exportUsageList(year, month);
    } catch (e) {
      console.error('질의 내역 엑셀 내려받기 실패:', e);
      setListError('엑셀을 내려받지 못했습니다.');
    } finally {
      setExporting(null);
    }
  };

  const periodLabel = trend ? `${monthTitle(trend.from)} – ${monthTitle(trend.to)}` : '';

  return (
    <>
      <Sidebar />
      <Header />
      <Box component="main" className="MainContent" sx={{ flex: 1 }}>
        <Paper
          sx={{
            height: { xs: '100dvh', lg: '100dvh' },
            display: 'flex',
            flexDirection: 'column',
            pt: { xs: 'var(--Header-height)', sm: 0 },
          }}
        >
          <MessagesPaneHeader menuTitle={menuInfo.selectedName} />

          <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#F8F8F8' }}>
            <div className={styles.page}>

              {/* ===== 이용 추이 ===== */}
              <section className={styles.card}>
                <div className={styles.cardHead}>
                  <div>
                    <Typography variant="h3" className={styles.cardTitle}>이용 추이</Typography>
                    <p className={styles.cardSub}>{periodLabel}</p>
                  </div>
                  <div className={styles.cardTools}>
                    <Select
                      size="small"
                      value={period}
                      onChange={(e) => setPeriod(String(e.target.value))}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      <MenuItem value={RECENT_12}>최근 12개월</MenuItem>
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={String(y)}>{y}년</MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadOutlinedIcon />}
                      disabled={!trend || exporting !== null}
                      onClick={handleExportTrend}
                    >
                      {exporting === 'trend' ? '준비 중…' : '추이 엑셀 받기'}
                    </Button>
                  </div>
                </div>

                {trendError && <p className={styles.state}>{trendError}</p>}

                {trend && !trendError && (
                  <>
                    <ul className={styles.facts}>
                      <li>총 질의 <b>{comma(trend.totalQueries)}</b>건</li>
                      <li>평균 MAU <b>{trend.avgMau}</b>명</li>
                      <li>
                        {period === RECENT_12 ? '기간 내 순이용자' : '연 순이용자'}{' '}
                        <b>{trend.uniqueUsers}</b>명
                      </li>
                      {trend.peakMonth && (
                        <li>최고 <b>{trend.peakMonth.replace('-0', '.').replace('-', '.')}</b> ({comma(trend.peakQueries)}건)</li>
                      )}
                    </ul>

                    <UsageTrendChart
                      points={trend.points}
                      selectedMonth={selectedMonth}
                      onSelectMonth={handleSelectMonth}
                    />
                  </>
                )}
              </section>

              {/* ===== 질의 내역 ===== */}
              <section className={styles.card}>
                <div className={styles.cardHead}>
                  <div>
                    <Typography variant="h3" className={styles.cardTitle}>질의 내역</Typography>
                    <p className={styles.cardSub}>{monthTitle(selectedMonth)} · 질의 일시 최신순</p>
                  </div>
                  <div className={styles.cardTools}>
                    <Select
                      size="small"
                      value={monthOptions.includes(selectedMonth) ? selectedMonth : ''}
                      onChange={(e) => handleSelectMonth(String(e.target.value))}
                      displayEmpty
                      sx={{ fontSize: '0.9rem' }}
                    >
                      {monthOptions.length === 0 && <MenuItem value="">데이터 없음</MenuItem>}
                      {monthOptions.map((m) => (
                        <MenuItem key={m} value={m}>{monthTitle(m)}</MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FileDownloadOutlinedIcon />}
                      disabled={!list || list.total === 0 || exporting !== null}
                      onClick={handleExportList}
                    >
                      {exporting === 'list' ? '준비 중…' : '전체 엑셀 받기'}
                    </Button>
                  </div>
                </div>

                <UsageLogTable
                  data={list}
                  loading={listLoading}
                  error={listError}
                  onPageChange={setPage}
                />
              </section>

            </div>
          </Box>
        </Paper>
      </Box>
    </>
  );
}
